// frontend/src/pages/MetricsDashboard.jsx
import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export default function MetricsDashboard() {
  const [occupationMetrics, setOccupationMetrics] = useState(null);
  const [cleaningMetrics, setCleaningMetrics] = useState(null);
  const [releaseMetrics, setReleaseMetrics] = useState(null);
  const [cleaningPerformance, setCleaningPerformance] = useState(null);
  const [flowMetrics, setFlowMetrics] = useState(null);
  const [timeRange, setTimeRange] = useState('today');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const [occRes, cleanRes, releaseRes, perfRes, flowRes] = await Promise.all([
          api.get('/metrics/occupation'),
          api.get('/metrics/cleaning-sla'),
          api.get('/metrics/bed-release-time'),
          api.get('/metrics/cleaning-performance'),
          api.get('/metrics/flow-efficiency')
        ]);

        setOccupationMetrics(occRes.data);
        setCleaningMetrics(cleanRes.data);
        setReleaseMetrics(releaseRes.data);
        setCleaningPerformance(perfRes.data);
        setFlowMetrics(flowRes.data);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [timeRange]);

  const renderOccupationChart = () => {
    if (!occupationMetrics?.by_sector) return null;

    const data = Object.entries(occupationMetrics.by_sector).map(([name, stats]) => ({
      name,
      ocupados: stats.occupied,
      disponíveis: stats.available
    }));

    return (
      <div className="card mb-4">
        <div className="card-header">
          <h5>Ocupação por Setor</h5>
        </div>
        <div className="card-body">
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="ocupados" fill="#8884d8" name="Ocupados" />
                <Bar dataKey="disponíveis" fill="#82ca9d" name="Disponíveis" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3">
            <div className="row text-center">
              <div className="col-md-4">
                <h4>{occupationMetrics.total_beds}</h4>
                <small className="text-muted">Total de Leitos</small>
              </div>
              <div className="col-md-4">
                <h4 className="text-warning">{occupationMetrics.occupied}</h4>
                <small className="text-muted">Ocupados</small>
              </div>
              <div className="col-md-4">
                <h4 className="text-success">{occupationMetrics.available}</h4>
                <small className="text-muted">Disponíveis</small>
              </div>
            </div>
            <div className="mt-2 text-center">
              <strong>Taxa de Ocupação: {occupationMetrics.occupancy_rate}%</strong>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCleaningSLA = () => {
    if (!cleaningMetrics) return null;

    const data = [
      { name: 'Dentro do SLA', value: cleaningMetrics.onTime },
      { name: 'Fora do SLA', value: cleaningMetrics.total - cleaningMetrics.onTime }
    ];

    return (
      <div className="card mb-4">
        <div className="card-header">
          <h5>Eficiência de Limpeza (SLA {cleaningMetrics.slaMinutes}min)</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6" style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="col-md-6 d-flex align-items-center">
              <div>
                <h2 className={cleaningMetrics.slaPercentage >= 90 ? 'text-success' : 'text-warning'}>
                  {cleaningMetrics.slaPercentage}%
                </h2>
                <p>de conformidade com o SLA</p>
                <p className="text-muted">
                  <strong>{cleaningMetrics.onTime}</strong> de <strong>{cleaningMetrics.total}</strong> leitos
                  higienizados em até {cleaningMetrics.slaMinutes} minutos
                </p>
                {cleaningMetrics.averageTime > 0 && (
                  <p className="text-info">
                    <strong>Tempo médio:</strong> {cleaningMetrics.averageTime} minutos
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReleaseTime = () => {
    if (!releaseMetrics) return null;

    return (
      <div className="card mb-4">
        <div className="card-header">
          <h5>Tempo Médio de Liberação de Leitos</h5>
        </div>
        <div className="card-body">
          <div className="row text-center">
            <div className="col-md-4">
              <h3 className="text-primary">{releaseMetrics.averageReleaseTimeMinutes}</h3>
              <p className="text-muted">Minutos</p>
            </div>
            <div className="col-md-4">
              <h3 className="text-info">{releaseMetrics.releaseCount}</h3>
              <p className="text-muted">Liberações Analisadas</p>
            </div>
            <div className="col-md-4">
              <h3 className="text-secondary">{releaseMetrics.bedsAnalyzed}</h3>
              <p className="text-muted">Leitos no Sistema</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCleaningPerformance = () => {
    if (!cleaningPerformance || cleaningPerformance.length === 0) return null;

    return (
      <div className="card mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0">Relatório Detalhado de Higienização</h5>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Leito</th>
                <th>Responsável</th>
                <th>Início</th>
                <th>Fim</th>
                <th>Duração</th>
              </tr>
            </thead>
            <tbody>
              {cleaningPerformance.map((session, idx) => (
                <tr key={idx}>
                  <td><strong>{session.bedNumber}</strong></td>
                  <td>{session.cleaner}</td>
                  <td>{new Date(session.startTime).toLocaleTimeString()}</td>
                  <td>{new Date(session.endTime).toLocaleTimeString()}</td>
                  <td>
                    <span className={`badge ${session.durationMinutes > 30 ? 'bg-warning' : 'bg-success'}`}>
                      {session.durationMinutes} min
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const downloadFlowCSV = () => {
    if (!flowMetrics || flowMetrics.length === 0) return;

    const headers = ['Data', 'Leito', 'Lavanderia (min)', 'Enfermagem (min)', 'Governanta (min)', 'Total (min)'];
    const csvContent = [
      headers.join(';'),
      ...flowMetrics.map(f =>
        [f.date, f.bedNumber, f.times.laundryWait, f.times.nursingWait, f.times.housekeeperWait, f.times.totalDuration].join(';')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'relatorio_fluxo_eficiencia.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const renderFlowEfficiency = () => {
    if (!flowMetrics) return null;

    return (
      <div className="card mb-4 print-section">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Eficiência do Fluxo de Admissão</h5>
          <div className="d-print-none">
            <button className="btn btn-outline-success btn-sm me-2" onClick={downloadFlowCSV}>
              <i className="bi bi-file-earmark-excel me-1"></i> Exportar CSV
            </button>
            <button className="btn btn-outline-primary btn-sm" onClick={handlePrint}>
              <i className="bi bi-printer me-1"></i> Imprimir
            </button>
          </div>
        </div>
        <div className="card-body">
          {/* Legenda de Indicadores */}
          <div className="alert alert-secondary mb-3">
            <strong>Entenda os Indicadores:</strong>
            <ul className="mb-0 mt-1 small">
              <li><strong>Tempo Lavanderia:</strong> Tempo entre a solicitação do kit (Check-in) e a entrega.</li>
              <li><strong>Tempo Enfermagem:</strong> Tempo entre a entrega do kit e a organização do leito.</li>
              <li><strong>Tempo Governanta:</strong> Tempo entre o leito organizado e a acomodação final.</li>
            </ul>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered text-center">
              <thead className="table-light">
                <tr>
                  <th>Leito</th>
                  <th>Lavanderia</th>
                  <th>Enfermagem</th>
                  <th>Governanta</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {flowMetrics.map((flow, idx) => (
                  <tr key={idx}>
                    <td className="text-start"><strong>{flow.bedNumber}</strong><br /><small className="text-muted">{flow.date}</small></td>
                    <td>{flow.times.laundryWait} min</td>
                    <td>{flow.times.nursingWait} min</td>
                    <td>{flow.times.housekeeperWait} min</td>
                    <td className="fw-bold bg-light">{flow.times.totalDuration} min</td>
                  </tr>
                ))}
                {flowMetrics.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-muted py-3">Nenhum fluxo completo registrado recentemente.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col-md-6">
          <h2>Métricas Operacionais</h2>
        </div>
        <div className="col-md-6 text-end">
          <select
            className="form-select w-auto d-inline-block"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="today">Hoje</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
          </select>
        </div>
      </div>

      {renderOccupationChart()}
      {renderCleaningSLA()}
      {renderReleaseTime()}
      {renderCleaningPerformance()}
      {renderFlowEfficiency()}
    </div>
  );
}
